import React, { useEffect, useRef, useState } from 'react';
import type { AtsMatch, AtsVerdict } from '../../lib/api';

// The ATS Resume AI's look: brand purple and gold, white cards with soft
// shadows, an animated score ring, and the small pieces every screen shares.

export const C = {
  purple: '#2b2240',
  purple2: '#4e3a69',
  gold: '#F0B429',
  orange: '#FF6535',
  ink: '#1f1a2e',
};

export const scoreColor = (v: number) => (v >= 75 ? '#16a34a' : v >= 50 ? '#d97706' : '#dc2626');

// Each model's short name and colour, so chips show without loading options
export const MODEL_META: Record<string, { name: string; color: string; icon: string }> = {
  'claude-haiku-5-5':  { name: 'Haiku 5.5',  color: '#0d9488', icon: '⚡' },
  'claude-sonnet-5-5': { name: 'Sonnet 5.5', color: '#2563eb', icon: '⚖️' },
  'claude-opus-5-5':   { name: 'Opus 5.5',   color: '#7c3aed', icon: '💎' },
  'claude-fable-5-1':  { name: 'Fable 5.1',  color: '#ea580c', icon: '🚀' },
};
export const modelName = (id: string) => MODEL_META[id]?.name ?? id;

export const rupees = (n: number) => (n > 0 && n < 1 ? `₹${n.toFixed(1)}` : `₹${Math.round(n).toLocaleString('en-IN')}`);
export const fmtWhen = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
export const fmtTimer = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// The keyframes the screens use (once per page)
export const AtsStyles: React.FC = () => (
  <style>{`
    .ats-fade { animation: atsFade .4s ease both; }
    .ats-pop { animation: atsPop .35s cubic-bezier(.2,.9,.3,1.3) both; }
    .ats-orb { animation: atsSpin 2.4s linear infinite; }
    .ats-glow { animation: atsGlow 2.2s ease-in-out infinite; }
    .ats-dot { animation: atsDot 1.2s ease-in-out infinite; }
    .ats-shimmer {
      background: linear-gradient(90deg, #2b2240 0%, #7c5cbf 40%, #F0B429 55%, #2b2240 75%);
      background-size: 200% auto; -webkit-background-clip: text; background-clip: text; color: transparent;
      animation: atsShimmer 2.6s linear infinite;
    }
    .ats-card { box-shadow: 0 1px 2px rgba(16,12,30,.04), 0 10px 30px -14px rgba(43,34,64,.22); }
    .ats-lift { transition: transform .2s ease, box-shadow .2s ease; }
    .ats-lift:hover { transform: translateY(-2px); box-shadow: 0 2px 4px rgba(16,12,30,.05), 0 18px 40px -16px rgba(43,34,64,.35); }
    @keyframes atsFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    @keyframes atsPop { from { opacity: 0; transform: scale(.94); } to { opacity: 1; transform: none; } }
    @keyframes atsSpin { to { transform: rotate(360deg); } }
    @keyframes atsGlow { 0%,100% { opacity: .55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
    @keyframes atsDot { 0%,80%,100% { transform: translateY(0); opacity: .4; } 40% { transform: translateY(-4px); opacity: 1; } }
    @keyframes atsShimmer { to { background-position: -200% center; } }
  `}</style>
);

// A number that counts up to its value
export function useCountUp(value: number, ms = 700) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / ms);
      const v = Math.round(a + (value - a) * (1 - Math.pow(1 - k, 3)));
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return shown;
}

export const ScoreRing: React.FC<{ value: number; size?: number; stroke?: number; label?: string; muted?: boolean }> = ({ value, size = 120, stroke = 10, label, muted }) => {
  const shown = useCountUp(value);
  const r = (size - stroke) / 2;
  const len = 2 * Math.PI * r;
  const color = muted ? '#9ca3af' : scoreColor(value);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#efedf5" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={len} strokeDashoffset={len * (1 - Math.max(0, Math.min(100, value)) / 100)}
          style={{ transition: 'stroke-dashoffset .8s cubic-bezier(.2,.8,.2,1), stroke .4s' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-black leading-none" style={{ color, fontSize: size * 0.26 }}>{shown}<span style={{ fontSize: size * 0.13 }}>%</span></span>
        {label && <span className="mt-1 font-bold uppercase tracking-wider text-gray-400" style={{ fontSize: Math.max(8, size * 0.075) }}>{label}</span>}
      </div>
    </div>
  );
};

type BtnKind = 'gold' | 'dark' | 'ghost' | 'soft' | 'danger';
const BTN: Record<BtnKind, string> = {
  gold: 'text-[#2b2240] shadow-[0_6px_18px_-6px_rgba(240,180,41,.7)] hover:brightness-105',
  dark: 'text-white shadow-[0_6px_18px_-8px_rgba(43,34,64,.8)] hover:brightness-110',
  ghost: 'bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:text-purple-800',
  soft: 'bg-purple-50 text-purple-800 hover:bg-purple-100',
  danger: 'bg-white text-red-600 border border-red-200 hover:bg-red-50',
};
export const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: BtnKind; size?: 'sm' | 'md' | 'lg' }> = ({ kind = 'ghost', size = 'md', className = '', style, ...rest }) => (
  <button {...rest}
    className={`inline-flex items-center justify-center gap-1.5 font-extrabold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
      size === 'sm' ? 'px-3 py-1.5 text-xs' : size === 'lg' ? 'px-6 py-3 text-sm' : 'px-4 py-2 text-xs'} ${BTN[kind]} ${className}`}
    style={{ ...(kind === 'gold' ? { background: `linear-gradient(135deg, ${C.gold}, #f7c948)` } : kind === 'dark' ? { background: `linear-gradient(135deg, ${C.purple}, ${C.purple2})` } : {}), ...style }} />
);

export const Card: React.FC<{ title?: React.ReactNode; icon?: string; right?: React.ReactNode; children: React.ReactNode; className?: string; pad?: boolean }> = ({ title, icon, right, children, className = '', pad = true }) => (
  <div className={`bg-white rounded-3xl border border-gray-100 ats-card ${pad ? 'p-5 sm:p-6' : ''} ${className}`}>
    {(title || right) && (
      <div className={`flex items-center justify-between gap-3 ${pad ? 'mb-4' : 'px-5 pt-5 mb-3'}`}>
        <p className="flex items-center gap-2 text-[15px] font-extrabold text-gray-900">
          {icon && <span className="w-8 h-8 rounded-xl flex items-center justify-center text-base bg-purple-50">{icon}</span>}
          {title}
        </p>
        {right}
      </div>
    )}
    {children}
  </div>
);

export const Pill: React.FC<{ bg: string; color: string; children: React.ReactNode; className?: string }> = ({ bg, color, children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${className}`} style={{ background: bg, color }}>{children}</span>
);

export const VERDICT: Record<AtsVerdict, { label: string; bg: string; color: string; icon: string }> = {
  eligible:     { label: 'Eligible',              bg: '#dcfce7', color: '#166534', icon: '✅' },
  not_eligible: { label: 'Not eligible',          bg: '#fee2e2', color: '#991b1b', icon: '⛔' },
  check:        { label: 'Check before applying', bg: '#fef3c7', color: '#92400e', icon: '⚠️' },
};

export const VerdictPill: React.FC<{ verdict: AtsVerdict | '' }> = ({ verdict }) => {
  if (!verdict) return null;
  const v = VERDICT[verdict];
  return <Pill bg={v.bg} color={v.color}>{v.icon} {v.label}</Pill>;
};

export const MATCH: Record<AtsMatch, { label: string; bars: number; color: string }> = {
  strong: { label: 'Strong match', bars: 3, color: '#16a34a' },
  medium: { label: 'Some match',   bars: 2, color: '#d97706' },
  weak:   { label: 'Weak match',   bars: 1, color: '#9ca3af' },
};

export const MatchMeter: React.FC<{ match: AtsMatch }> = ({ match }) => {
  const m = MATCH[match];
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-bold whitespace-nowrap" style={{ color: m.color }}>
      <span className="flex gap-0.5">{[1, 2, 3].map(i => <span key={i} className="w-2.5 h-3.5 rounded-sm" style={{ background: i <= m.bars ? m.color : '#e5e7eb' }} />)}</span>
      {m.label}
    </span>
  );
};

export const ModelChip: React.FC<{ id: string; prefix?: string; className?: string }> = ({ id, prefix, className = '' }) => {
  const m = MODEL_META[id];
  if (!id) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border whitespace-nowrap ${className}`} style={{ borderColor: `${m?.color ?? '#9ca3af'}40`, color: m?.color ?? '#4b5563' }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m?.color ?? '#9ca3af' }} />
      {prefix}{modelName(id)}
    </span>
  );
};

// ① Upload → ② Match report → ③ Your resume
export const Stepper: React.FC<{ at: 1 | 2 | 3 }> = ({ at }) => {
  const steps = ['Upload', 'Match report', 'Your resume'];
  return (
    <div className="flex items-center gap-2 sm:gap-3">
      {steps.map((s, i) => {
        const n = i + 1;
        const done = n < at, now = n === at;
        return (
          <React.Fragment key={s}>
            {i > 0 && <div className="flex-1 h-0.5 rounded-full min-w-[16px]" style={{ background: n <= at ? C.gold : '#e5e7eb' }} />}
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all"
                style={done ? { background: C.gold, color: C.purple } : now ? { background: C.purple, color: '#fff', boxShadow: '0 0 0 4px rgba(43,34,64,.12)' } : { background: '#f3f4f6', color: '#9ca3af' }}>
                {done ? '✓' : n}
              </span>
              <span className={`text-xs font-bold whitespace-nowrap ${now ? 'text-gray-900' : 'text-gray-400'} ${now ? '' : 'hidden sm:inline'}`}>{s}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const Modal: React.FC<{ open: boolean; onClose: () => void; title: React.ReactNode; sub?: React.ReactNode; wide?: boolean; children: React.ReactNode; footer?: React.ReactNode }> = ({ open, onClose, title, sub, wide, children, footer }) => {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#1a1428]/50 backdrop-blur-sm ats-fade" onClick={onClose} />
      <div className={`relative w-full ${wide ? 'sm:max-w-4xl' : 'sm:max-w-xl'} max-h-[92vh] flex flex-col bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl ats-pop`}>
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-lg font-extrabold text-gray-900">{title}</p>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-700 shrink-0">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-3xl">{footer}</div>}
      </div>
    </div>
  );
};

export const Spinner: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <span className="inline-block rounded-full border-purple-200 border-t-purple-700 animate-spin" style={{ width: size, height: size, borderWidth: Math.max(2, size / 8) }} />
);

export const TypingDots: React.FC = () => (
  <span className="inline-flex gap-1 items-center">
    {[0, 1, 2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-purple-400 ats-dot" style={{ animationDelay: `${i * 0.15}s` }} />)}
  </span>
);
